import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createLocalStorageQueueStore,
  enqueueScan,
  pruneTerminal,
  queueCounts,
  replayQueue,
  type QueueCounts,
  type QueueStore,
  type QueuedScanRecord,
  type ReplaySummary,
} from "./offline-queue";
import type { ScanSyncItem } from "./scan-contract";
import type { SubmitScanResult } from "./submit-scan";

// React seam over the pure offline queue. Owns the single in-memory queue snapshot, persists every
// transition through the QueueStore (crash-interrupted `syncing` records are recovered to `queued`
// by the store's load), and replays on the three triggers the spec names: app load, the browser
// `online` event, and manual retry. Enqueueing while online also kicks an immediate flush. A ref
// guard keeps replay passes from overlapping; replay itself is sequential FIFO.

export interface UseOfflineQueueResult {
  records: QueuedScanRecord[];
  counts: QueueCounts;
  flushing: boolean;
  /** Add a captured scan to the durable queue (persisted before this returns). */
  enqueue: (item: ScanSyncItem) => void;
  /** Replay queued scans now (manual retry). Resolves null if a pass is already running/empty. */
  flush: () => Promise<ReplaySummary | null>;
}

export function useOfflineQueue(options: {
  submit: (item: ScanSyncItem) => Promise<SubmitScanResult>;
  store?: QueueStore;
  isOnline?: () => boolean;
}): UseOfflineQueueResult {
  const storeRef = useRef<QueueStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = options.store ?? createLocalStorageQueueStore();
  }
  const store = storeRef.current;

  const [records, setRecords] = useState<QueuedScanRecord[]>(() => store.load());
  const recordsRef = useRef(records);
  const [flushing, setFlushing] = useState(false);
  const flushingRef = useRef(false);

  // Latest-value refs so the stable callbacks never go stale (and never restart effects).
  const submitRef = useRef(options.submit);
  submitRef.current = options.submit;
  const isOnlineRef = useRef(options.isOnline ?? defaultIsOnline);
  isOnlineRef.current = options.isOnline ?? defaultIsOnline;

  const apply = useCallback(
    (next: readonly QueuedScanRecord[]) => {
      const pruned = pruneTerminal(next);
      store.save(pruned);
      recordsRef.current = pruned;
      setRecords(pruned);
    },
    [store],
  );

  const flush = useCallback(async (): Promise<ReplaySummary | null> => {
    if (flushingRef.current) {
      return null;
    }
    if (!recordsRef.current.some((record) => record.state === "queued")) {
      return null;
    }
    flushingRef.current = true;
    setFlushing(true);
    try {
      return await replayQueue(
        recordsRef.current,
        (item) => submitRef.current(item),
        (snapshot) => apply(snapshot),
      );
    } finally {
      flushingRef.current = false;
      setFlushing(false);
    }
  }, [apply]);

  const enqueue = useCallback(
    (item: ScanSyncItem) => {
      apply(enqueueScan(recordsRef.current, item, new Date().toISOString()));
      if (isOnlineRef.current()) {
        void flush();
      }
    },
    [apply, flush],
  );

  // App-load replay of anything persisted from a previous session + replay on reconnect.
  useEffect(() => {
    if (isOnlineRef.current()) {
      void flush();
    }
    const onOnline = (): void => {
      void flush();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [flush]);

  const counts = useMemo(() => queueCounts(records), [records]);

  return { records, counts, flushing, enqueue, flush };
}

export function defaultIsOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}
